import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faPenToSquare,
  faPlus,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import type { CustomerContact } from '@/services/customer.service';

/**
 * Contact picker for the selected location.
 *
 * A native <select> renders one line of text per option, which is why the phone
 * number was being fetched and thrown away. Whoever is receiving equipment needs
 * the number to hand — that is the whole reason a contact is captured — so this
 * is a listbox rather than a select.
 *
 * Contacts belong to exactly one location and cannot exist without it, so this
 * is inert until a location is chosen and says so.
 */

export function contactName(c: CustomerContact): string {
  return `${c.firstName} ${c.lastName}`.trim();
}

export function ContactPicker({
  contacts,
  value,
  onChange,
  onAdd,
  onEdit,
  locationChosen,
  disabled = false,
}: {
  contacts: CustomerContact[];
  value: string;
  onChange: (contactId: string) => void;
  onAdd: () => void;
  onEdit: (contact: CustomerContact) => void;
  /** Contacts hang off a location; without one there is nothing to list. */
  locationChosen: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = contacts.find((c) => c.id === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const inert = disabled || !locationChosen;

  return (
    <div className="flex items-start gap-2">
      <div className="relative min-w-0 flex-1" ref={rootRef}>
        <button
          type="button"
          disabled={inert}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          title={!locationChosen ? 'Choose where it came from first' : undefined}
          className="flex min-h-[46px] w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FontAwesomeIcon icon={faUser} className="h-4 w-4 shrink-0 text-primary" />
          {selected ? (
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{contactName(selected)}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {[selected.phone, selected.designation].filter(Boolean).join(' · ') ||
                  'No phone on record'}
              </span>
            </span>
          ) : (
            <span className="flex-1 truncate text-muted-foreground">
              {locationChosen ? 'Choose a person…' : 'Choose where it came from first'}
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            className="h-3 w-3 shrink-0 text-muted-foreground"
          />
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 rounded-lg border border-border bg-card shadow-lg">
            <ul role="listbox" className="max-h-64 overflow-y-auto p-1">
              {contacts.length === 0 ? (
                <li className="px-3 py-3 text-center text-[13px] text-muted-foreground">
                  Nobody on file at this location yet.
                </li>
              ) : (
                contacts.map((c) => (
                  <li key={c.id} role="option" aria-selected={c.id === value}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(c.id);
                        setOpen(false);
                      }}
                      className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
                    >
                      <FontAwesomeIcon
                        icon={faUser}
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-primary"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {contactName(c)}
                          {c.isPrimary ? ' ★' : ''}
                        </span>
                        {c.phone && <span className="block truncate text-xs">{c.phone}</span>}
                        {c.designation && (
                          <span className="block truncate text-xs text-muted-foreground">
                            ({c.designation})
                          </span>
                        )}
                      </span>
                      {c.id === value && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="mt-1 h-3 w-3 shrink-0 text-primary"
                        />
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
            <div className="border-t border-border p-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onAdd();
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-semibold text-primary hover:bg-muted"
              >
                <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                Add a contact here
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => selected && onEdit(selected)}
        disabled={inert || !selected}
        title="Edit this contact"
        aria-label="Edit contact"
        className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
